import { useEffect, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, Info, TriangleAlert, XCircle, X } from 'lucide-react';
import { TooltipProvider, cn } from '@micarahr/web-ui';
import { Sidebar } from './sidebar';
import { CommandBar } from './command-bar';
import { ConfirmHost } from '../module/confirm';
import { IdleSignOut } from './idle-sign-out';
import { usePermission, useUiStore, useWorkspaceStore } from '../../stores';
import { approvalService, attendanceAdminService, dashboardService } from '../../services';
import { LIVE_QUERY, queryKeys } from '../../lib/query';

/**
 * The application shell.
 *
 * Full height, full width, no centred column: an HR administrator's monitor is
 * the workspace, and a 900px content well in the middle of a 1440px screen
 * wastes the half of it they are paying for (§7).
 */
export function AppShell({ children }: { children: ReactNode }) {
  const setSidebarCollapsed = useUiStore((state) => state.setSidebarCollapsed);
  const degraded = useWorkspaceStore((state) => state.degraded);

  // Below a tablet landscape there is not enough width for both the sidebar and
  // a master-detail workspace, so the sidebar collapses to icons automatically.
  useEffect(() => {
    const query = window.matchMedia('(max-width: 1279px)');
    const apply = () => {
      if (query.matches) setSidebarCollapsed(true);
    };
    apply();
    query.addEventListener('change', apply);
    return () => query.removeEventListener('change', apply);
  }, [setSidebarCollapsed]);

  const { can } = usePermission();

  /*
   * Badge counts, fetched only by someone the server will answer. A payroll
   * officer, recruiter or auditor holds no `approvals.view`, and every page
   * they opened fired a refused request for a badge they would never see.
   */
  const approvals = useQuery({
    queryKey: queryKeys.approvals.list({ view: 'assigned' }),
    queryFn: () => approvalService.list({ view: 'assigned' }),
    enabled: can('approvals.view'),
    /*
     * Other people decide these too — in another tab, another reviewer, the
     * manager's app — and the 30-second cache kept counting a request the
     * queue no longer listed (8 over a list of 7). Every decision here
     * invalidates `approvals`; the rest are caught on focus and by realtime.
     */
    ...LIVE_QUERY,
  });
  const attention = useQuery({
    queryKey: queryKeys.dashboard.attention(),
    queryFn: () => dashboardService.attention(),
    enabled: can('employees.view'),
  });

  /*
   * The review queue's own count.
   *
   * This was the number of *attention items* of the geofence kind — and the
   * server rolls every flagged record into one such item, so the badge read 1
   * over a queue of twenty-seven. Asking the queue's query for a single row
   * and reading its total makes the badge and the screen the same number by
   * construction; the key sits under `attendance`, so a decision refreshes it.
   */
  const flagged = useQuery({
    queryKey: queryKeys.attendance.list({ flaggedOnly: true, badge: true }),
    enabled: can('attendance.review'),
    queryFn: () => attendanceAdminService.list({ flaggedOnly: true, page: 1, pageSize: 1 }),
  });

  const badges = {
    approvals: approvals.data?.length ?? 0,
    attention: attention.data?.length ?? 0,
    flagged: flagged.data?.total ?? 0,
  };

  return (
    <TooltipProvider>
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      <div className="flex h-screen w-full overflow-hidden bg-canvas">
        <Sidebar badges={badges} />
        <div className="flex min-w-0 flex-1 flex-col">
          <CommandBar />
          {degraded ? (
            <div
              role="alert"
              className="flex shrink-0 items-center gap-2 border-b border-warning-border bg-warning-soft px-4 py-2"
            >
              <TriangleAlert className="h-4 w-4 shrink-0 text-warning" aria-hidden />
              <p className="text-sm text-ink-secondary">
                Browser storage is unavailable, so this session is running in memory. Everything
                works, but nothing you change will survive a reload.
              </p>
            </div>
          ) : null}
          <main id="main-content" className="min-h-0 flex-1 overflow-hidden">
            {children}
          </main>
        </div>
      </div>
      <Toaster />
      <ConfirmHost />
      <IdleSignOut />
    </TooltipProvider>
  );
}

/**
 * Toasts.
 *
 * Feedback for actions whose result is not visible on screen — an approval in a
 * queue that then disappears from that queue needs to say what happened.
 */
function Toaster() {
  const toasts = useUiStore((state) => state.toasts);
  const dismiss = useUiStore((state) => state.dismissToast);

  if (toasts.length === 0) return null;

  const icons = {
    success: CheckCircle2,
    danger: XCircle,
    warning: TriangleAlert,
    info: Info,
  } as const;

  const tones = {
    success: 'border-success-border bg-success-soft text-success',
    danger: 'border-danger-border bg-danger-soft text-danger',
    warning: 'border-warning-border bg-warning-soft text-warning',
    info: 'border-info-border bg-info-soft text-info',
  } as const;

  return (
    <div
      className="pointer-events-none fixed bottom-4 right-4 z-[60] flex w-[380px] max-w-[calc(100vw-2rem)] flex-col gap-2"
      role="region"
      aria-label="Notifications"
    >
      {toasts.map((toast) => {
        const Icon = icons[toast.tone];
        return (
          <div
            key={toast.id}
            role="status"
            className="pointer-events-auto flex animate-slide-in-right items-start gap-3 rounded-lg border border-line bg-surface p-3 shadow-lg"
          >
            <span
              className={cn(
                'mt-px flex h-6 w-6 shrink-0 items-center justify-center rounded-full border',
                tones[toast.tone],
              )}
            >
              <Icon className="h-3.5 w-3.5" aria-hidden />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-base font-medium text-ink">{toast.title}</p>
              {toast.message ? (
                <p className="mt-0.5 text-sm text-ink-secondary">{toast.message}</p>
              ) : null}
            </div>
            <button
              type="button"
              onClick={() => dismiss(toast.id)}
              className="shrink-0 rounded p-0.5 text-ink-muted hover:bg-neutral-soft"
              aria-label="Dismiss"
            >
              <X className="h-3.5 w-3.5" aria-hidden />
            </button>
          </div>
        );
      })}
    </div>
  );
}
